// src/components/Control/Control.tsx
// Страница робота /control/:robotId. Здесь живёт всё общее: сессия Zenoh
// (подключается сама), телеметрия, система событий, клавиатура, голос.
// Дальше — одна из двух раскладок: desktop/планшет/телефон в landscape
// (карта + камера + свёрнутое управление) или mobile portrait (карта во весь
// экран, камера поверх, управление в нижней шторке, нижняя навигация).
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/operator.css';
import '../../styles/features.css';
import { useZenoh } from '../../hooks/useZenoh';
import { useClientPresence } from '../../hooks/useClientPresence';
import { useAuth } from '../../auth/AuthContext';
import { putJson } from '../../hooks/useZenohSubscription';
import { Store } from './robot/store';
import { initialTelemetry, useTelemetry, Telemetry, chassisPref } from './robot/telemetry';
import { initialEvents, useEventSystem, EventsState, notifier, pushEvent } from './robot/events';
import { Drive, useKeyboardDrive } from './robot/drive';
import { VoiceController } from './robot/voice';
import { SnapshotController } from './robot/snapshots';
import { RobotApi, RobotProvider, createUiStore, uiActions, CamChoice, Screen, ZenohInfo } from './robot/RobotContext';
import { useStore } from './robot/store';
import DesktopLayout from './Operator/DesktopLayout';
import MobileLayout from './Operator/MobileLayout';
import VoiceOverlay from './Operator/VoiceOverlay';
import Toasts from './Operator/Toasts';

// Mobile-раскладка: узкий экран в портретной ориентации. Телефон в landscape
// получает desktop-подобную раскладку (карта слева, камера справа).
function useIsMobile(): boolean {
  const query = '(max-width: 767px) and (orientation: portrait)';
  const [mobile, setMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

// Команды голосом, которые выполняет сам интерфейс (в контексте экрана)
function localIntent(text: string, ui: ReturnType<typeof uiActions>, stop: () => void): boolean {
  const t = text.toLowerCase().replace(/^робот[,\s]*/, '').trim();
  if (/^(стоп|остановись|стой)\b/.test(t)) {
    stop(); // плюс роботу уйдёт сама команда — не возвращаем true
    return false;
  }
  const cam: [RegExp, CamChoice][] = [[/камер\S* (1|один|первую)/, '0'], [/камер\S* (2|два|вторую)/, '1'], [/(обе|две) камеры/, 'split']];
  for (const [re, c] of cam) {
    if (re.test(t) && /(включи|покажи|переключи|открой)/.test(t)) {
      ui.setCam(c);
      return true;
    }
  }
  const screens: [RegExp, Screen][] = [[/открой карту|покажи карту/, 'map'], [/открой камеру|покажи камеру/, 'camera'], [/открой няню|режим няни на экран/, 'nanny']];
  for (const [re, s] of screens) {
    if (re.test(t)) {
      ui.go(s);
      return true;
    }
  }
  if (/открой настройки/.test(t)) {
    ui.openSettings();
    return true;
  }
  return false;
}

const Control: React.FC = () => {
  const { robotId = '001' } = useParams<{ robotId: string }>();
  const navigate = useNavigate();
  const z = useZenoh();
  const { session } = z;
  const isMobile = useIsMobile();

  const stores = useMemo(() => {
    const events = new Store<EventsState>(initialEvents);
    return {
      telemetry: new Store<Telemetry>({ ...initialTelemetry, chassis: chassisPref(robotId) }),
      events,
      drive: new Drive(),
      voice: new VoiceController(events),
      snapshots: new SnapshotController(events),
      ui: createUiStore(),
      zenoh: new Store<ZenohInfo>({ state: 'idle', messages: [] }),
    };
    // хранилища — одни на страницу; смену робота обрабатывает эффект ниже
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Состояние связи и сообщения для «Диагностики» — через хранилище, чтобы не перерисовывать всю страницу
  useEffect(() => {
    stores.zenoh.set({ state: z.state, messages: z.messages });
  }, [z.state, z.messages, stores]);

  // Смена робота: телеметрия и история — с нуля
  useEffect(() => {
    stores.telemetry.set({ ...initialTelemetry, chassis: chassisPref(robotId) });
    stores.events.set(initialEvents);
  }, [robotId, stores]);

  stores.drive.setTarget(session, robotId);
  stores.voice.setTarget(session, robotId);
  stores.snapshots.setTarget(session, robotId);

  // Подключаемся сразу; отключение — вручную в «Настройки → Zenoh»
  const { connect, disconnect } = z;
  useEffect(() => {
    connect('/zenoh-ws', `robot/${robotId}/status`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotId]);

  useTelemetry(stores.telemetry, session, robotId);
  // Эта страница — один из клиентов робота (токен), и счёт всех клиентов
  const { user } = useAuth();
  useClientPresence(session, robotId, user, clients => stores.telemetry.set({ clients }));
  useEventSystem(stores.events, stores.telemetry, session, robotId);

  const actions = useMemo(() => uiActions(stores.ui), [stores]);

  const api: RobotApi = useMemo(() => {
    const stop = () => {
      stores.drive.emergencyStop();
      pushEvent(stores.events, { source: 'system', level: 'ok', text: '🛑 STOP отправлен — робот остановлен' }, true);
    };
    return {
      robotId,
      robotName: `Робот ${robotId}`,
      session,
      zenoh: {
        store: stores.zenoh,
        connect: () => connect('/zenoh-ws', `robot/${robotId}/status`),
        disconnect,
      },
      telemetry: stores.telemetry,
      events: stores.events,
      drive: stores.drive,
      voice: stores.voice,
      snapshots: stores.snapshots,
      ui: stores.ui,
      notify: notifier(stores.events),
      stop,
      goBack: () => navigate('/dashboard'),
    };
  }, [robotId, session, connect, disconnect, stores, navigate]);

  stores.voice.localIntent = text => localIntent(text, actions, api.stop);

  const keyboard = useStore(stores.drive.store, d => d.keyboard);
  // Pi без шасси: ехать нечему — клавиши движения и STOP не перехватываем
  const chassis = useStore(stores.telemetry, t => t.chassis);
  useKeyboardDrive(stores.drive, keyboard && chassis, {
    onFlash: actions.flash,
    onDescribeMap: () => putJson(session, `robot/${robotId}/vision/cmd`, { cmd: 'describe_map', speak: true }),
    onStop: () => {
      api.stop();
      actions.flash('Стоп');
    },
  });

  useEffect(() => () => {
    stores.drive.dispose();
    stores.voice.dispose();
    stores.snapshots.dispose();
  }, [stores]);

  // Операторский экран — на всю высоту окна, без прокрутки страницы
  useEffect(() => {
    document.body.classList.add('op-body');
    return () => document.body.classList.remove('op-body');
  }, []);

  // Вкладка браузера: какой робот открыт
  useEffect(() => {
    document.title = `Робот ${robotId} · roboflot`;
    return () => {
      document.title = 'roboflot';
    };
  }, [robotId]);

  return (
    <RobotProvider value={api}>
      {isMobile ? <MobileLayout /> : <DesktopLayout />}
      <VoiceOverlay />
      <Toasts />
    </RobotProvider>
  );
};

export default Control;
